import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

const VIEWPORT_MARGIN = 8;

/**
 * Generic hover/tap tooltip primitive (daily-insights-engine-trd.md, Section
 * 1 / Section 4 Requirement 2 -- no existing generic tooltip component was
 * found anywhere in cq-app/src/components, confirmed via directory listing).
 *
 * Wraps a single trigger element (`children`) and shows floating `content`
 * on hover (desktop), focus (keyboard), or tap/click (touch -- hover doesn't
 * exist on touch devices). By default it is an absolute-positioned popover
 * above the trigger inside a relatively-positioned wrapper (no portal), which
 * is fine for callers rendering inside a normal-flow tile that doesn't clip
 * overflow.
 *
 * sidebar-nav-revamp/trd.md R7 added an opt-in portal / right-placement mode
 * for triggers inside overflow-clipping containers (the Sidebar). All new
 * props default to the previous behavior, so existing callers (InsightTile)
 * are unaffected.
 *
 * @param {React.ReactNode} content - tooltip body (string or JSX).
 * @param {React.ReactNode} children - the single trigger element.
 * @param {string} [maxWidth='260px'] - CSS max-width for the tooltip bubble.
 * @param {string} [textAlign='left'] - CSS text-align for the tooltip body.
 * @param {'inline-block'|'block'} [display='inline-block'] - wrapper display.
 * @param {boolean} [disabled=false] - render `children` with no tooltip wiring.
 * @param {boolean} [portal=false] - render the bubble into document.body with
 *   `position: fixed` (escapes ancestor overflow clipping).
 * @param {'top'|'right'} [placement='top'] - 'right' is only honored in portal
 *   mode.
 * @param {number} [offset] - gap in px between anchor and bubble (default 6).
 * @param {React.RefObject} [anchorRef] - with placement='right', the bubble's
 *   left edge is this element's right edge + offset instead of the trigger's.
 * @param {React.RefObject} [truncationRef] - when given, the tooltip only
 *   shows if that element is truncated (scrollWidth > clientWidth) at show time.
 * @param {boolean} [triggerOnClick=true] - whether a click toggles the tooltip.
 */
const Tooltip = ({
  content,
  children,
  maxWidth = '260px',
  textAlign = 'left',
  display = 'inline-block',
  disabled = false,
  portal = false,
  placement = 'top',
  offset = 6,
  anchorRef,
  truncationRef,
  triggerOnClick = true,
}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const tooltipId = useId();
  const wrapperRef = useRef(null);
  const bubbleRef = useRef(null);

  const usePortalMode = portal;

  const show = () => {
    if (truncationRef) {
      const el = truncationRef.current;
      if (!el || !(el.scrollWidth > el.clientWidth)) {
        setVisible(false);
        return;
      }
    }
    if (usePortalMode && wrapperRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();
      if (placement === 'right') {
        const anchorRight = anchorRef?.current
          ? anchorRef.current.getBoundingClientRect().right
          : triggerRect.right;
        setPos({
          left: anchorRight + offset,
          top: triggerRect.top + triggerRect.height / 2,
        });
      } else {
        setPos({
          left: triggerRect.left,
          top: triggerRect.top - offset,
        });
      }
    }
    setVisible(true);
  };

  const hide = () => setVisible(false);

  // Clamp vertically inside the viewport once the bubble can be measured.
  useLayoutEffect(() => {
    if (!visible || !usePortalMode || placement !== 'right' || !bubbleRef.current || !pos) return;
    const height = bubbleRef.current.offsetHeight;
    if (!height) return;
    const half = height / 2;
    const minTop = VIEWPORT_MARGIN + half;
    const maxTop = window.innerHeight - VIEWPORT_MARGIN - half;
    const clamped = Math.min(Math.max(pos.top, minTop), Math.max(minTop, maxTop));
    if (clamped !== pos.top) setPos((p) => ({ ...p, top: clamped }));
  }, [visible, usePortalMode, placement, pos]);

  if (disabled) {
    return children;
  }

  let bubble = null;
  if (visible) {
    if (usePortalMode && pos) {
      const portalStyle = {
        textAlign,
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transform: placement === 'right' ? 'translateY(-50%)' : 'translateY(-100%)',
        '--tooltip-max-width': maxWidth,
      };
      bubble = createPortal(
        <span
          role="tooltip"
          id={tooltipId}
          ref={bubbleRef}
          className={`${styles.tooltip} ${styles.portalTooltip}`}
          style={portalStyle}
        >
          {content}
        </span>,
        document.body
      );
    } else if (!usePortalMode) {
      bubble = (
        <span role="tooltip" id={tooltipId} className={styles.tooltip} style={{ textAlign }}>
          {content}
        </span>
      );
    }
  }

  return (
    <span
      ref={wrapperRef}
      className={styles.wrapper}
      style={{ display, '--tooltip-max-width': maxWidth }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={triggerOnClick ? () => (visible ? hide() : show()) : undefined}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}
      {bubble}
    </span>
  );
};

export default Tooltip;
